import React from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { isBefore, subDays } from 'date-fns';
import { ReservationCancellationType } from '@classpass/protobuf-definitions/lib/web/models/partners/reservation';

import Avatar from 'components/Avatar';
import Badge from 'components/Badge';
import Dot from 'components/Dot';
import { Dropdown, Menu } from 'components/Dropdown';
import Message from 'components/Message';
import { PFE_ICONS } from 'constants/icons';
import { FEEDBACK_REPORT_AGE_LIMIT_DAYS } from 'constants/logic';
import { reservationPropTypes, schedulePropTypes } from 'constants/propTypes';
import { getReservationFeedbackAlreadyReported } from 'features/ReservationFeedback/ReservationFeedback.helpers';
import { classNameWithModifiers as withMods } from 'helpers/class-name';
import { makePath } from 'helpers/link-generation';
import Segment from 'helpers/segment';
import { refreshRosterAfterReservationFeedback } from './YieldManagement.saga';

import './RosterListItem.scss';

const FEEDBACK_ACTION = 'FEEDBACK_ACTION';
const EMAIL_MEMBER_ACTION = 'EMAIL_MEMBER_ACTION';
const VIEW_WAIVER_ACTION = 'VIEW_WAIVER_ACTION';
const CANCEL_RESERVATION_ACTION = 'CANCEL_RESERVATION_ACTION';

const messages = defineMessages({
  ariaLabelTakeAction: {
    id: 'yield-management.roster-list-item.aria-label-take-action',
    defaultMessage: 'Take action',
  },
  ariaLabelMarkAttended: {
    id: 'yield-management.roster-list-item.aria-label-mark-attended',
    defaultMessage: 'Mark attended',
  },
  ariaLabelMarkMissed: {
    id: 'yield-management.roster-list-item.aria-label-mark-missed',
    defaultMessage: 'Mark missed',
  },
  feedback: {
    id: 'yield-management.roster-list-item.feedback',
    defaultMessage: 'Report member issue',
  },
  classCancelled: {
    id: 'yield-management.roster-list-item.class-cancelled',
    defaultMessage: 'Canceled by studio',
  },
  lateCancel: {
    id: 'yield-management.roster-list-item.late-cancel',
    defaultMessage: 'Late cancel',
  },
  lateCancelWaive: {
    id: 'yield-management.roster-list-item.late-cancel-waive',
    defaultMessage: 'Late cancel -- policy exception',
  },
  reservationQueued: {
    id: 'yield-management.roster-list-item.reservation-queued',
    defaultMessage: 'Reservation processing',
  },
  attended: {
    id: 'yield-management.roster-list-item.attended',
    defaultMessage: 'Attended',
  },
  missed: {
    id: 'yield-management.roster-list-item.missed',
    defaultMessage: 'Missed',
  },
  new: {
    id: 'yield-management.roster-list-item.new',
    defaultMessage: 'New to your business',
  },
  email: {
    id: 'yield-management.roster-list-item.email',
    defaultMessage: 'Email member',
  },
  subject: {
    id: 'yield-management.roster-list-item.email-subject',
    defaultMessage:
      'Update regarding your {startDateTime, date, time}, {startDateTime, date, long-w-dow} session at {venueNameSubtitle}',
  },
  waiver: {
    id: 'yield-management.roster-list-item.waiver',
    defaultMessage: 'Waiver',
  },
  viewWaiver: {
    id: 'yield-management.roster-list-item.view-waiver',
    defaultMessage: 'View signed waiver',
  },
  outOfDate: {
    id: 'yield-management.roster-list-item.out-of-date',
    defaultMessage: 'Out of date',
  },
  cancelReservation: {
    id: 'yield-management.roster-list-item.cancelReservation',
    defaultMessage: 'Cancel reservation',
  },
});

const ReservationActionMenu = ({
  id,
  schedule,
  isCancellable,
  onSelect,
  isOpen,
  waiverVersionId,
  waiverVersionIdSigned,
}) => {
  const items = [
    {
      key: EMAIL_MEMBER_ACTION,
      text: () => <Message message={messages.email} />,
    },
  ];

  if (waiverVersionIdSigned) {
    const outOfDate = waiverVersionId !== waiverVersionIdSigned;
    items.push({
      key: VIEW_WAIVER_ACTION,
      text: () => (
        <>
          <Message message={messages.viewWaiver} />
          {outOfDate && <Message as={Badge} className="ml-huge" message={messages.outOfDate} warn solid />}
        </>
      ),
    });
  }

  const isOlderThan30Days = isBefore(schedule.startDateTime, subDays(new Date(), FEEDBACK_REPORT_AGE_LIMIT_DAYS));

  if (schedule.isInThePast && !isOlderThan30Days) {
    items.push({
      key: FEEDBACK_ACTION,
      text: () => <Message message={messages.feedback} />,
    });
  }

  if (isCancellable) {
    items.push({
      key: CANCEL_RESERVATION_ACTION,
      text: () => (
        <Message
          className={withMods('roster-list-item__action-item', {
            cancel: true,
          })}
          message={messages.cancelReservation}
        />
      ),
    });
  }

  return (
    <Menu
      id={id}
      items={items}
      onSelect={onSelect}
      getKey={option => option.key}
      getText={option => option.key}
      renderMenuItem={(key, isSelected, item) => item.text()}
      isOpen={isOpen}
    />
  );
};

ReservationActionMenu.propTypes = {
  onSelect: PropTypes.func,
  id: PropTypes.string,
  isOpen: PropTypes.bool,
  schedule: PropTypes.shape(schedulePropTypes),
  isCancellable: PropTypes.bool,
  waiverVersionId: PropTypes.number,
  waiverVersionIdSigned: PropTypes.number,
};

const RosterListItem = ({
  attendanceStatus,
  cancellationType,
  canUpdateAttendance,
  className,
  date: startDate,
  emailMember,
  feedback,
  fullName,
  isClassCancelled,
  isLateCancel,
  isLateCancelWaived,
  isQueued,
  isScheduleAfterUpdateAttendanceTimeRange,
  isScheduleBeforeUpdateAttendanceTimeRange,
  openModal,
  profileUrl,
  reservationId,
  schedule,
  scheduleId,
  startDatetime,
  userId,
  venueId,
  venueName,
  venueSubtitle,
  visitNumberToVenue,
  waiverId,
  waiverVersionId,
  waiverVersionIdSigned,
  waiverSignedTimestamp,
  updateReservationAttendance,
  isCancellable,
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const navigate = useNavigate();

  const handleMarkAttended = () => {
    if (attendanceStatus === true) {
      return;
    }
    updateReservationAttendance(reservationId, true);
  };

  const handleMarkMissed = () => {
    if (attendanceStatus === false) {
      return;
    }
    updateReservationAttendance(reservationId, false);
  };

  const handleActionSelected = action => {
    switch (action.key) {
      case VIEW_WAIVER_ACTION: {
        const viewSignedWaiverHref = makePath.viewSignedWaiver({
          venueId,
          startDate,
          scheduleId,
          reservationId,
          waiverId,
          waiverVersionId: waiverVersionIdSigned,
        });
        navigate(viewSignedWaiverHref);
        break;
      }

      case FEEDBACK_ACTION: {
        Segment.event('Click Report User', { VID: venueId, ReservationID: reservationId });
        openModal('ReservationFeedbackModalV2', {
          reservationId,
          venueId,
          fullName,
          profileUrl,
          classEntityType: schedule?.entityType,
          onReported: () => dispatch(refreshRosterAfterReservationFeedback({ venueId, scheduleId })),
          ...getReservationFeedbackAlreadyReported(feedback),
        });
        break;
      }

      case EMAIL_MEMBER_ACTION: {
        const { startDateTime } = schedule;

        openModal('EmailModal', {
          onSubmit: emailMember,
          title: <Message message={messages.email} />,
          to: fullName,
          subject: (
            <Message
              message={messages.subject}
              values={{
                startDateTime,
                venueNameSubtitle: `${venueName}${venueSubtitle ? ` (${venueSubtitle})` : ''}`,
              }}
            />
          ),
          initialValues: {
            startDateTime,
            recipients: [{ user_id: userId }],
          },
        });
        break;
      }

      case CANCEL_RESERVATION_ACTION: {
        openModal('ReservationCancellationModal', {
          reservationId,
          fullName,
          className,
          scheduleId,
          startDate,
          startDatetime,
          venueId,
          venueName,
          venueSubtitle,
        });
        break;
      }

      default:
        return undefined;
    }
  };

  const renderWaiverStatus = () => {
    const outOfDate = waiverVersionId !== waiverVersionIdSigned;

    return (
      waiverVersionId && (
        <div className="roster-list-item__waiver-status text--sm mr-m">
          <Message message={messages.waiver} />
          <Dot
            data-testid="waiver-status-dot"
            className="ml-sp"
            color={waiverSignedTimestamp && !outOfDate ? 'green' : 'red'}
          />
        </div>
      )
    );
  };

  const renderAttendanceOrStatus = () => {
    let attendenceStatus;

    if (canUpdateAttendance || !isScheduleAfterUpdateAttendanceTimeRange) {
      const isSessionInFuture = !(schedule?.isInProgress || schedule?.isInThePast);

      attendenceStatus = (
        <>
          <button
            type="button"
            aria-label={intl.formatMessage(messages.ariaLabelMarkAttended)}
            aria-pressed={attendanceStatus === true}
            className="roster-list-item__action-wrap"
            onClick={handleMarkAttended}>
            <Isvg
              className={withMods('roster-list-item__icon', {
                attended: true,
                selected: attendanceStatus === true,
              })}
              uniquifyIDs={false}
              src={PFE_ICONS.attended}
            />
          </button>
          <button
            type="button"
            aria-label={intl.formatMessage(messages.ariaLabelMarkMissed)}
            aria-pressed={attendanceStatus === false}
            className="roster-list-item__action-wrap"
            disabled={isSessionInFuture}
            aria-disabled={isSessionInFuture}
            onClick={handleMarkMissed}>
            <Isvg
              className={withMods('roster-list-item__icon', {
                missed: true,
                selected: attendanceStatus === false,
                disabled: isSessionInFuture,
              })}
              uniquifyIDs={false}
              src={PFE_ICONS.missed}
            />
          </button>
        </>
      );
    }

    if (isLateCancel) {
      attendenceStatus = <Message className="text--light" message={messages.lateCancel} />;
    } else if (isLateCancelWaived) {
      attendenceStatus = <Message className="text--light" message={messages.lateCancelWaive} />;
    } else if (isQueued) {
      attendenceStatus = <Message className="text--light" message={messages.reservationQueued} />;
    } else if (isClassCancelled) {
      attendenceStatus = <Message className="text--light" message={messages.classCancelled} />;
    } else if (!canUpdateAttendance && isScheduleBeforeUpdateAttendanceTimeRange) {
      if (cancellationType === ReservationCancellationType.MISSED_CLASS || attendanceStatus === false) {
        attendenceStatus = <Message className="text--light" message={messages.missed} />;
      } else {
        attendenceStatus = <Message className="text--light" message={messages.attended} />;
      }
    }

    return (
      <div className="roster-list-item__actions">
        {renderWaiverStatus()}
        {attendenceStatus}
        <div className="roster-list-item__action-wrap">
          <Dropdown
            attachment="right"
            Toggle={({ isOpen, toggleMenu }) => (
              <button
                type="button"
                aria-label={intl.formatMessage(messages.ariaLabelTakeAction)}
                aria-haspopup
                aria-expanded={isOpen}
                aria-controls="roster-list-item__action-menu"
                onClick={() => {
                  Segment.event('User roster menu clicked', { venueId });
                  toggleMenu();
                }}
                className="roster-list-item__menu-icon">
                <Isvg className="svg-three-dots" uniquifyIDs={false} src={PFE_ICONS.threeDots} />
              </button>
            )}
            Content={props => (
              <ReservationActionMenu
                id="roster-list-item__action-menu"
                onSelect={handleActionSelected}
                schedule={schedule}
                isCancellable={isCancellable}
                waiverVersionId={waiverVersionId}
                waiverVersionIdSigned={waiverVersionIdSigned}
                {...props}
              />
            )}
          />
        </div>
      </div>
    );
  };

  return (
    <li className="roster-list-item">
      <div className="grid grid--flush grid--middle">
        <div className="grid__item 1/8 roster-list-item__avatar">
          <Avatar name={fullName} url={profileUrl} />
        </div>
        <div className="grid__item 1/2 roster-list-item__info">
          <div className="roster-list-item__name">{fullName}</div>
          <div className="roster-list-item__tags">
            {visitNumberToVenue === 1 && <Message className="roster-list-item__tag" message={messages.new} />}
          </div>
        </div>
        <div className="grid__item 3/8 roster-list-item__status">{renderAttendanceOrStatus()}</div>
      </div>
    </li>
  );
};

RosterListItem.propTypes = {
  hasEnabledWaiver: PropTypes.bool,
  ...reservationPropTypes,
};

export default RosterListItem;
